import { AppointmentModel } from "@devexpress/dx-react-scheduler";
import { AppointmentTooltip as DXAppointmentTooltip } from "@devexpress/dx-react-scheduler-material-ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useMemo } from "react";

import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PermContactCalendarOutlinedIcon from "@mui/icons-material/PermContactCalendarOutlined";
import SmsIcon from "@mui/icons-material/Sms";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import { format, parseISO } from "date-fns";

import { useAppSelector } from "app/hooks";
import { CustomPrintFormPreviewProvider } from "components/custom-print-form-preview/use-custom-print-form-preview.hook";
import { PDFGenerationV3Provider } from "components/pdf-generation-v3/pdf-generation-v3.hook";
import { PreviewPdfFormProvider } from "components/preview-pdf-form-dialog/use-preview-pdf-form.hook";
import {
  APPOINTMENT_STATUS_ARRIVED,
  APPOINTMENT_STATUS_COMPLETED,
  NOTIFICATION_METHOD_EMAIL
} from "constants/appointments";
import {
  APPOINTMENT_FORMS_FEATURE,
  DISPLAY_SUPERVISING_PROVIDER,
  ROOM_BOOKING_DURATION_OVERRIDE,
  SHOW_PATIENT_TAGS_ON_APPOINTMENT_FORM
} from "constants/feature-flags";
import {
  selectCurrentPractice,
  selectNumberOfPracticeClinics
} from "features/auth/auth-slice";
import IntakeFormsCount from "features/scheduling/appointment-form/fields/intake-forms-count.component";
import AppointmentFormsDownload from "features/scheduling/appointment-forms-download";
import FormTemplatesProvider from "features/scheduling/appointment-forms-download/use-form-templates.hook";
import useAppointments from "features/scheduling/hooks/use-appointments.hook";
import {
  useIsOpportunity,
  useOpportunityFieldEnabled
} from "hooks/use-is-opportunity.hook";
import { useGetPatientAssignedTagsQuery } from "services/assigned-tags";
import { useGetIntakeFormRequestsQuery } from "services/onboarding-requests";
import type { Appointment } from "services/types/appointments.type";
import type { AssignedTag } from "services/types/assigned-tag.type";

import AppointmentTags from "../../components/appointment-tags.component";
import { classes } from "../appointment-preview.component";
import AppointmentChecklist from "./appointment-checklist.component";
import AppointmentPatient from "./appointment-patient.component";
import PreviewContentRow from "./preview-content-row.component";

type AppointmentPreviewContentProps = {
  appointmentData: DXAppointmentTooltip.ContentProps["appointmentData"] &
    AppointmentModel;
  setCanAppointmentBeCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  setHasCustomChecklist: React.Dispatch<React.SetStateAction<boolean>>;
};
export default function AppointmentPreviewContent({
  appointmentData,
  setCanAppointmentBeCompleted,
  setHasCustomChecklist
}: AppointmentPreviewContentProps) {
  const { refetchEvents, updateAppointmentMetaData } = useAppointments();
  const isOpportunity = useIsOpportunity();
  const opportunityFieldEnabled = useOpportunityFieldEnabled();
  const flags = useFlags();
  const isAppointmentFormsEnabled = flags[APPOINTMENT_FORMS_FEATURE];
  const showPatientTags = flags[SHOW_PATIENT_TAGS_ON_APPOINTMENT_FORM];
  const displaySupervisingProvider = flags[DISPLAY_SUPERVISING_PROVIDER];
  const roomOverrideEnabled = flags[ROOM_BOOKING_DURATION_OVERRIDE];
  const currentPractice = useAppSelector(selectCurrentPractice);
  const numberOfPracticeClinics =
    useAppSelector(selectNumberOfPracticeClinics) || 0;
  const multiClinic = numberOfPracticeClinics > 1;

  const { data: intakeFormsData = [] } = useGetIntakeFormRequestsQuery(
    {
      patientId: appointmentData?.patient?.id,
      appointment: (appointmentData?.modelId || appointmentData?.id)?.toString()
    },
    {
      skip:
        !(appointmentData?.modelId || appointmentData?.id) ||
        !appointmentData?.patient?.id,
      refetchOnMountOrArgChange: true
    }
  );

  const { data: assigned_tags } = useGetPatientAssignedTagsQuery(
    {
      object_id: appointmentData?.patient?.id
    },
    {
      skip:
        !showPatientTags ||
        !appointmentData?.patient?.id ||
        appointmentData?.patient?.assigned_tags !== undefined
    }
  );

  const patientTagsToUse =
    appointmentData?.patient?.assigned_tags || assigned_tags;

  const tagsAvailable = showPatientTags && Boolean(patientTagsToUse?.length);

  const [showPatientTagsTooltip, setShowPatientTagsTooltip] =
    React.useState(false);

  const handlePopoverOpen = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (tagsAvailable) {
        setShowPatientTagsTooltip(true);
      }
    },
    [tagsAvailable]
  );

  const handlePopoverClose = React.useCallback(() => {
    setShowPatientTagsTooltip(false);
  }, []);

  const handleTagUpdate = React.useCallback(
    (updatedTags: AssignedTag[]) => {
      updateAppointmentMetaData(
        Number(appointmentData.modelId || appointmentData.id),
        {
          assignedTags: updatedTags
        }
      );
      refetchEvents();
    },
    [
      updateAppointmentMetaData,
      refetchEvents,
      appointmentData.modelId,
      appointmentData.id
    ]
  );

  const notificationContent = useMemo(() => {
    if (appointmentData?.shouldNotify) {
      if (appointmentData?.notifyBothChannels) {
        return `Text & Email Notifications Enabled`;
      }

      if (
        Number(appointmentData?.notificationMethod) ===
        NOTIFICATION_METHOD_EMAIL
      ) {
        return `Email Notifications Enabled`;
      }

      return `Text Notifications Enabled`;
    }

    return `Notifications Disabled`;
  }, [appointmentData]);

  const {
    patient,
    webPatient,
    staffMember: { fullName, is_active },
    room,
    clinic,
    id,
    modelId,
    notes,
    status,
    shouldNotify,
    appointmentType,
    referralSource,
    subReferralSource,
    assignedTags,
    supervising_provider,
    is_direct_supervisor,
    room_start_moment,
    room_end_moment
  } = appointmentData;

  const roomTimeLabel = useMemo(() => {
    if (!roomOverrideEnabled || !room_start_moment || !room_end_moment) {
      return null;
    }
    try {
      return `${format(parseISO(room_start_moment), "h:mm a")} – ${format(parseISO(room_end_moment), "h:mm a")}`;
    } catch {
      return null;
    }
  }, [roomOverrideEnabled, room_start_moment, room_end_moment]);

  const appointmentWithId = useMemo(
    () =>
      ({
        ...appointmentData,
        id: modelId || id
      }) as Appointment,
    [appointmentData, id, modelId]
  );

  if (!appointmentData?.staffMember) return null;

  return (
    <Grid container alignItems="flex-start" className={classes.titleContainer}>
      <Stack flex={1}>
        <PreviewContentRow
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          Icon={PermContactCalendarOutlinedIcon}
          content={
            <AppointmentPatient
              patient={patient || webPatient}
              assignedTags={patientTagsToUse}
              showPatientTagsTooltip={showPatientTagsTooltip}
            />
          }
          highlighted
        />

        <AppointmentTags
          containerSx={{
            ml: "75px",
            maxWidth: "457px",
            mb: 1.5,
            mt: 1.0,
            opacity: tagsAvailable && showPatientTagsTooltip ? 0.1 : 1
          }}
          appointmentId={Number(modelId || id)}
          tags={assignedTags}
          beforeContent={
            Boolean(assignedTags?.length) && (
              <Typography variant="body1">Appointment&nbsp;Tags:</Typography>
            )
          }
          onTagChange={handleTagUpdate}
        />

        {multiClinic && (
          <PreviewContentRow
            Icon={LocationOnOutlinedIcon}
            content={clinic.name}
          />
        )}
        {referralSource && (
          <PreviewContentRow
            Icon={AccountCircleOutlinedIcon}
            content={referralSource?.name}
          />
        )}
        {subReferralSource && (
          <PreviewContentRow
            Icon={AccountCircleOutlinedIcon}
            content={subReferralSource?.name}
          />
        )}
        <PreviewContentRow
          Icon={PeopleOutlinedIcon}
          testId="appointment-preview-staff"
          content={fullName}
          sx={{ textDecoration: !is_active ? "line-through" : undefined }}
        />
        {/* Shown for BOTH reasons a visit can be an Opportunity. Limiting it to the
            hand-marked case made absence mean either "not an Opportunity" or "an
            Opportunity by its type", which is what staff read during a reschedule call. */}
        {opportunityFieldEnabled &&
          isOpportunity(appointmentData, appointmentType) && (
            <PreviewContentRow
              Icon={StarOutlineIcon}
              testId="opportunity-row"
              content="Opportunity"
            />
          )}
        {displaySupervisingProvider && supervising_provider && (
          <PreviewContentRow
            Icon={PeopleOutlinedIcon}
            testId="appointment-preview-supervising-provider"
            content={`${supervising_provider.fullName}${is_direct_supervisor ? " (Direct)" : " (Supervising)"}`}
            sx={{
              textDecoration: !supervising_provider.is_active
                ? "line-through"
                : undefined,
              color: grey[600]
            }}
          />
        )}
        <PreviewContentRow
          Icon={LocalHospitalOutlinedIcon}
          content={
            room?.name
              ? roomTimeLabel
                ? `${room.name} (${roomTimeLabel})`
                : room.name
              : "No Room"
          }
          sx={{ color: room?.name ? undefined : grey[700] }}
        />
        {currentPractice?.enable_appointment_communications && (
          <PreviewContentRow
            Icon={SmsIcon}
            content={notificationContent}
            sx={{ color: shouldNotify ? undefined : grey[700] }}
          />
        )}
        {patient?.id && Boolean(intakeFormsData[0]?.forms_requested_count) && (
          <PreviewContentRow
            Icon={AssignmentTurnedInOutlinedIcon}
            content={
              <IntakeFormsCount
                patientId={patient?.id}
                form={intakeFormsData[0]}
              />
            }
            containerSx={{ mb: notes ? 0 : 2.5 }}
          />
        )}

        {isAppointmentFormsEnabled && (
          <PreviewContentRow
            content={
              <PDFGenerationV3Provider>
                <CustomPrintFormPreviewProvider
                  providerSignatureEnabled
                  aiFeatureEnabled
                >
                  <FormTemplatesProvider
                    appointment={appointmentWithId}
                    patientId={patient?.id || patient}
                  >
                    <PreviewPdfFormProvider
                      formName="form"
                      patientId={patient?.id || patient}
                      showSignButton
                    >
                      <AppointmentFormsDownload />
                    </PreviewPdfFormProvider>
                  </FormTemplatesProvider>
                </CustomPrintFormPreviewProvider>
              </PDFGenerationV3Provider>
            }
            sx={{ width: "100%" }}
          />
        )}

        {notes && (
          <PreviewContentRow
            Icon={ContentPasteOutlinedIcon}
            content={
              <Typography sx={{ whiteSpace: "pre-line" }}>{notes}</Typography>
            }
            sx={{ mb: 2.5 }}
          />
        )}

        {[APPOINTMENT_STATUS_ARRIVED, APPOINTMENT_STATUS_COMPLETED].includes(
          status
        ) && (
          <PreviewContentRow
            content={
              <AppointmentChecklist
                appointmentId={Number(modelId || id)}
                patientId={Number(patient.id)}
                hasGeneratedInvoice={Boolean(appointmentType.generate_sale)}
                setCanAppointmentBeCompleted={setCanAppointmentBeCompleted}
                setHasCustomChecklist={setHasCustomChecklist}
              />
            }
          />
        )}
      </Stack>
    </Grid>
  );
}
